// src/components/site-editor/FieldRenderer.jsx
import React from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import ArrayEditor from './ArrayEditor';
import { isFieldEditable } from './editableFields';

/**
 * Field Renderer - Whitelist-based component that only renders editable text fields
 *
 * Uses editableFields.js whitelist to determine what's editable.
 * Only renders text/copy fields - no structural changes allowed.
 *
 * Handles:
 * - Strings (short) → Input
 * - Strings (long) → Textarea
 * - Arrays → ArrayEditor (read-only structure, editable text within items)
 */
const FieldRenderer = ({ fieldKey, value, path, onChange, section, templateType }) => {
  // Format field name for display
  const formatFieldName = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  const displayName = formatFieldName(fieldKey);

  // Check if this field is editable according to whitelist
  const editableStatus = isFieldEditable(section, fieldKey, templateType);

  // If not editable at all, don't render anything
  if (editableStatus === false) {
    return null;
  }

  // If it's an array field with editable subfields
  if (Array.isArray(editableStatus)) {
    // editableStatus contains the list of editable subfields
    return (
      <ArrayEditor
        label={displayName}
        items={value || []}
        path={path}
        onChange={onChange}
        editableFields={editableStatus} // Pass which fields are editable within items
        readOnly={true} // No add/remove buttons
      />
    );
  }

  // If it's a simple editable field (string)
  if (editableStatus === true) {
    const fieldType = typeof value === 'string' && value.length > 100 ? 'textarea' : 'input';

    if (fieldType === 'textarea') {
      return (
        <div className="space-y-2">
          <Label htmlFor={path}>{displayName}</Label>
          <Textarea
            id={path}
            value={value || ''}
            onChange={(e) => onChange(path, e.target.value)}
            placeholder={`Enter ${displayName.toLowerCase()}...`}
            rows={6}
            className="font-normal"
          />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label htmlFor={path}>{displayName}</Label>
        <Input
          id={path}
          value={value || ''}
          onChange={(e) => onChange(path, e.target.value)}
          placeholder={`Enter ${displayName.toLowerCase()}...`}
        />
      </div>
    );
  }

  // Shouldn't reach here, but fallback to null
  return null;
};

export default FieldRenderer;
